import { useCallback, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

import { HEAT_PANE } from '../MapPanes';
import normalizeHeatT from './normalize';

export interface CanvasHeatConfig {
  gradient: Record<string, string>;
  minOpacity: number;
  maxOpacity: number;
  fixedMax?: number;
  /** Piso do domínio (default 0) — permite variáveis com valores negativos. */
  fixedMin?: number;
}

/** Parses both '#rrggbb' and 'rgb(r, g, b)' color strings. */
function parseColor(color: string): [number, number, number] {
  if (color.startsWith('#')) {
    const h = color.replace('#', '');
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }
  const m = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (m) return [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)];
  return [0, 0, 0];
}

function gradientColorRgb(
  gradient: Record<string, string>,
  t: number,
): [number, number, number] {
  const stops = Object.entries(gradient)
    .map(([k, v]) => ({ pos: parseFloat(k), color: v }))
    .sort((a, b) => a.pos - b.pos);

  if (!stops.length) return [255, 255, 255];
  if (t <= stops[0].pos) return parseColor(stops[0].color);

  const last = stops[stops.length - 1];
  if (t >= last.pos) return parseColor(last.color);

  const idx = stops.findIndex(
    (s, i) => i < stops.length - 1 && t >= s.pos && t <= stops[i + 1].pos,
  );
  if (idx === -1) return parseColor(last.color);

  const lo = stops[idx];
  const hi = stops[idx + 1];
  const ratio = (t - lo.pos) / (hi.pos - lo.pos);
  const [r0, g0, b0] = parseColor(lo.color);
  const [r1, g1, b1] = parseColor(hi.color);
  return [
    Math.round(r0 + (r1 - r0) * ratio),
    Math.round(g0 + (g1 - g0) * ratio),
    Math.round(b0 + (b1 - b0) * ratio),
  ];
}

export default function CanvasHeatLayer({
  points,
  config,
  legend,
}: {
  points: [number, number, number][];
  config: CanvasHeatConfig | undefined;
  legend?: L.Control;
}) {
  const map = useMap();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dataRef = useRef({ points, config });

  useEffect(() => {
    dataRef.current = { points, config };
  });

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !map) return;

    const { points: pts, config: cfg } = dataRef.current;
    const size = map.getSize();
    const topLeft = map.containerPointToLayerPoint([0, 0] as L.PointExpression);

    L.DomUtil.setPosition(canvas, topLeft);
    canvas.width = size.x;
    canvas.height = size.y;

    const ctx = canvas.getContext('2d');
    if (!ctx || !cfg || pts.length === 0) {
      ctx?.clearRect(0, 0, size.x, size.y);
      return;
    }

    ctx.clearRect(0, 0, size.x, size.y);

    const {
      gradient, minOpacity, maxOpacity, fixedMax, fixedMin,
    } = cfg;
    const max = fixedMax ?? pts.reduce((acc, [,, v]) => Math.max(acc, v), 1);
    const min = fixedMin ?? 0;
    const pixelRadius = Math.max(6, 22 * (2 ** (map.getZoom() - 6)));

    ctx.globalAlpha = 1;

    pts.forEach(([lat, lng, value]) => {
      const t = normalizeHeatT(value, min, max);
      const opacity = minOpacity + (maxOpacity - minOpacity) * t;
      if (opacity <= 0) return;

      const [r, g, b] = gradientColorRgb(gradient, t);
      const pt = map.latLngToContainerPoint([lat, lng]);
      const { x, y } = pt;

      const radGrad = ctx.createRadialGradient(x, y, 0, x, y, pixelRadius);
      radGrad.addColorStop(0, `rgba(${r},${g},${b},${opacity})`);
      radGrad.addColorStop(1, `rgba(${r},${g},${b},0)`);

      ctx.fillStyle = radGrad;
      ctx.beginPath();
      ctx.arc(x, y, pixelRadius, 0, Math.PI * 2);
      ctx.fill();
    });
  }, [map]);

  // Canvas mount/unmount
  useEffect(() => {
    if (!map) return () => {};

    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.pointerEvents = 'none';
    canvasRef.current = canvas;
    // Pane proprio, abaixo das divisas e do alerta. O fallback cobre o uso fora
    // do InteractiveMap, unico lugar que cria os panes.
    const pane = map.getPane(HEAT_PANE) ?? map.getPanes().overlayPane;
    pane.appendChild(canvas);

    const onZoomStart = () => { canvas.style.opacity = '0'; };
    const onZoomEnd = () => { canvas.style.opacity = ''; redraw(); };

    map.on('zoomstart', onZoomStart);
    map.on('zoomend', onZoomEnd);
    map.on('moveend resize', redraw);
    redraw();

    return () => {
      map.off('zoomstart', onZoomStart);
      map.off('zoomend', onZoomEnd);
      map.off('moveend resize', redraw);
      canvas.parentNode?.removeChild(canvas);
      canvasRef.current = null;
    };
  }, [map, redraw]);

  // Redraw when data changes
  useEffect(() => {
    redraw();
  }, [points, config, redraw]);

  // Legend lifecycle
  useEffect(() => {
    if (!map || !legend) return () => {};
    map.addControl(legend);
    return () => {
      try {
        map.removeControl(legend);
      } catch (_) {
        // map may already be removed (same parent-first cleanup order issue)
      }
    };
  }, [map, legend]);

  return null;
}
